import { useEffect, useState } from "react";
import { toast } from "sonner";
import { WhatsappLogo, PaperPlaneTilt, Plus, X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const LEAD_FIELDS = [
  { v: "name", l: "Lead name" }, { v: "course", l: "Course" }, { v: "city", l: "City" },
  { v: "campaign_name", l: "Campaign name" }, { v: "phone", l: "Phone" }];

export const SpeedToLead = ({ onChanged }) => {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [channelId, setChannelId] = useState("");
  const [template, setTemplate] = useState("");
  const [vars, setVars] = useState([]);
  const [testPhone, setTestPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/speedlead/settings").then(({ data }) => {
    setS(data);
    setEnabled(!!data.enabled);
    setApiKey(data.api_key || "");
    setChannelId(data.channel_id || "");
    setTemplate(data.template_name || "");
    setVars(Object.entries(data.template_vars || {}).map(([k, f]) => ({ k, f })));
  }).catch(() => setS({}));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        enabled, api_key: apiKey.trim(), channel_id: channelId.trim(), template_name: template.trim(),
        template_vars: Object.fromEntries(vars.filter((r) => r.k.trim()).map((r) => [r.k.trim(), r.f])),
      };
      if (apiSecret.trim()) body.api_secret = apiSecret.trim();
      await api.put("/speedlead/settings", body);
      toast.success(enabled ? "WhatsApp auto-reply is ON — new leads get messaged within 5 minutes" : "Settings saved");
      setApiSecret("");
      load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save settings", { duration: 8000 });
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) { toast.error("Enter a phone number for the test"); return; }
    setTesting(true);
    try {
      await api.post("/speedlead/test", { phone: testPhone.trim() });
      toast.success(`Test WhatsApp sent to ${testPhone} — check the phone`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed", { duration: 9000 });
    } finally { setTesting(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="speedlead-open-btn"
        className={`inline-flex items-center gap-1.5 rounded-full border text-[11px] font-bold px-3.5 h-9 transition-colors ${
          s?.enabled ? "border-[#25D366]/40 bg-[#25D366]/10 text-[#128C4A] hover:bg-[#25D366]/20"
                     : "border-[#E5E7EB] bg-white text-[#525866] hover:bg-[#F3F4F6]"}`}>
        <WhatsappLogo size={15} weight="fill" className={s?.enabled ? "text-[#25D366]" : "text-[#8a90a0]"} />
        WhatsApp Auto-Reply {s?.enabled ? "· ON" : ""}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="speedlead-dialog">
          <DialogHeader><DialogTitle className="font-heading font-extrabold flex items-center gap-2">
            <WhatsappLogo size={20} weight="fill" className="text-[#25D366]" /> WhatsApp Auto-Reply (Gallabox)
          </DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-xs text-[#8a90a0]">Every new Facebook lead gets a WhatsApp template message within ~5 minutes. Credentials: Gallabox → Settings → API keys · Channel ID: Settings → WhatsApp Channel.</p>
            <div className="flex items-center justify-between rounded-lg border border-[#E5E7EB] px-4 py-3">
              <div>
                <div className="text-xs font-bold text-[#0A0D14]">Auto-message new leads</div>
                <div className="text-[11px] text-[#8a90a0]">Checks for new leads every 5 minutes</div>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="speedlead-enable-switch" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-[#0A0D14]">API Key</label>
                <Input value={apiKey} onChange={(e) => setApiKey(e.target.value)} className="mt-1 h-9 text-xs font-mono" data-testid="speedlead-api-key" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#0A0D14]">API Secret</label>
                <Input value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} type="password"
                  placeholder={s?.has_secret ? "•••••• (saved)" : ""} className="mt-1 h-9 text-xs font-mono" data-testid="speedlead-api-secret" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#0A0D14]">Channel ID</label>
                <Input value={channelId} onChange={(e) => setChannelId(e.target.value)} className="mt-1 h-9 text-xs font-mono" data-testid="speedlead-channel-id" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#0A0D14]">Template name (approved)</label>
                <Input value={template} onChange={(e) => setTemplate(e.target.value)} placeholder="new_lead_welcome" className="mt-1 h-9 text-xs font-mono" data-testid="speedlead-template" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-[#0A0D14]">Template variables → lead fields</label>
                <button onClick={() => setVars((v) => [...v, { k: "", f: "name" }])} data-testid="speedlead-add-var"
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-[#0052FF]"><Plus size={12} weight="bold" /> Add</button>
              </div>
              {vars.length === 0 && <p className="text-[11px] text-[#8a90a0] mt-1">No variables — fine if your template has none.</p>}
              <div className="space-y-1.5 mt-1.5">
                {vars.map((r, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <Input value={r.k} onChange={(e) => setVars((v) => v.map((x, j) => (j === i ? { ...x, k: e.target.value } : x)))}
                      placeholder="Variable name (exact case)" className="h-8 text-xs font-mono flex-1" data-testid={`speedlead-var-key-${i}`} />
                    <Select value={r.f} onValueChange={(f) => setVars((v) => v.map((x, j) => (j === i ? { ...x, f } : x)))}>
                      <SelectTrigger className="h-8 w-40 text-xs font-semibold" data-testid={`speedlead-var-field-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{LEAD_FIELDS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                    <button onClick={() => setVars((v) => v.filter((_, j) => j !== i))} className="text-[#8a90a0] hover:text-[#c73030]"><X size={14} weight="bold" /></button>
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={save} disabled={busy} className="w-full rounded-full bg-[#0A0D14] hover:bg-black text-white font-bold h-10" data-testid="speedlead-save-btn">
              {busy ? "Saving…" : "Save settings"}
            </Button>
            <div className="rounded-lg border border-dashed border-[#c9cdd6] p-3">
              <label className="text-xs font-bold text-[#0A0D14]">Send a test message</label>
              <div className="flex items-center gap-2 mt-1.5">
                <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="98765 43210" className="h-9 text-xs flex-1" data-testid="speedlead-test-phone" />
                <Button onClick={sendTest} disabled={testing} size="sm" className="rounded-full bg-[#25D366] hover:bg-[#1eb658] text-white font-bold h-9" data-testid="speedlead-test-btn">
                  <PaperPlaneTilt size={13} weight="fill" className="mr-1" /> {testing ? "Sending…" : "Test"}
                </Button>
              </div>
              <p className="text-[10px] text-[#8a90a0] mt-1.5">Saves must be done first — the test uses your saved credentials.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
